import React from "react";
import { Button, Chip, Logo, PawMark, ShieldIcon, HeartIcon } from "./ui";
import { bruceSeed } from "./data";

/* -------------------------------------------------------------------------- */
/* Onboarding / landing screen                                                */
/* -------------------------------------------------------------------------- */

export default function Onboarding({ onRegister, onLogin }) {
  return (
    <div
      className="min-h-screen bg-[#FBFCFF]"
      data-testid="onboarding-screen"
    >
      {/* Top bar */}
      <header className="mx-auto flex max-w-6xl items-center justify-between px-5 py-5 md:px-8">
        <Logo size="md" />
        <button
          onClick={onLogin}
          data-testid="onboarding-login-link"
          className="hidden text-sm font-bold text-[#0B2D4D] transition hover:text-[#FF5C5C] md:inline-flex"
        >
          ¿Ya tienes cuenta? <span className="ml-1 underline underline-offset-4">Entrar</span>
        </button>
      </header>

      <main className="mx-auto max-w-6xl px-5 pb-16 pt-4 md:px-8 md:pt-8">
        <div className="grid items-center gap-10 md:grid-cols-[1.05fr_1fr] md:gap-14">
          {/* Left — copy */}
          <div className="pc-rise">
            <div className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs font-bold text-[#0B2D4D] shadow-sm">
              <span style={{ color: "#FF5C5C" }}>
                <PawMark size={14} />
              </span>
              Conecta, cuida y protege a tu perro
            </div>

            <h1 className="mt-5 font-display text-4xl font-black leading-[1.02] text-[#0B2D4D] sm:text-5xl lg:text-6xl">
              Todo para la vida de tu perro,
              <br className="hidden sm:block" /> <span className="italic text-[#FF5C5C]">en una sola app</span>
            </h1>

            <p className="mt-5 max-w-xl text-base leading-relaxed text-slate-500 md:text-lg">
              Conecta con otros padres de perros, lleva su salud contigo, encuentra ayuda cuando la
              necesites y forma parte de una comunidad responsable.
            </p>

            <div className="mt-7 flex flex-col gap-3 sm:flex-row">
              <Button
                variant="primary"
                onClick={onRegister}
                testId="onboarding-register-btn"
                className="px-7 py-4 text-base"
              >
                Unirme a PawCita
              </Button>
              <Button
                variant="outline"
                onClick={onLogin}
                testId="onboarding-login-btn"
                className="px-7 py-4 text-base"
              >
                Entrar
              </Button>
            </div>

            {/* Benefits */}
            <div className="mt-10 grid grid-cols-2 gap-3 sm:grid-cols-4">
              <Benefit emoji="🛡️" label="Perfiles verificados" />
              <Benefit emoji="🩺" label="Historial veterinario" />
              <Benefit emoji="🌿" label="Encuentros seguros" />
              <Benefit emoji="🐾" label="Comunidad PawCita" />
            </div>
          </div>

          {/* Right — Bruce hero */}
          <div className="pc-rise relative">
            <div className="relative overflow-hidden rounded-[40px] bg-gradient-to-br from-emerald-50 via-white to-rose-50 p-3 shadow-[0_40px_80px_-40px_rgba(11,45,77,0.35)]">
              <div className="pc-paws absolute inset-0 opacity-60" aria-hidden="true" />
              <div className="relative overflow-hidden rounded-[32px]">
                <img
                  src={bruceSeed.image}
                  alt="Bruce, un Jack Russell Terrier"
                  className="h-[460px] w-full object-cover md:h-[540px]"
                  style={{ objectPosition: "center top" }}
                  data-testid="hero-bruce-image"
                />
                {/* Badge */}
                <div
                  className="absolute right-5 top-5 inline-flex items-center gap-2 rounded-full bg-white/95 px-4 py-2 font-bold text-[#0B2D4D] shadow-lg"
                  data-testid="hero-bruce-badge"
                >
                  <span>Bruce está buscando match</span>
                  <span className="text-lg">🐶</span>
                </div>

                {/* Floating info card */}
                <div className="absolute bottom-5 left-5 right-5 rounded-3xl border border-white/60 bg-white/90 p-4 shadow-xl backdrop-blur">
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <p className="font-display text-xl font-black text-[#0B2D4D]">
                        {bruceSeed.name} · {bruceSeed.breed}
                      </p>
                      <p className="text-sm text-slate-500">
                        {bruceSeed.age} años · {bruceSeed.sex} · {bruceSeed.location}
                      </p>
                    </div>
                    <span
                      className="flex h-11 w-11 items-center justify-center rounded-2xl text-white"
                      style={{ background: "#FF5C5C" }}
                      aria-hidden="true"
                    >
                      <HeartIcon filled size={22} />
                    </span>
                  </div>
                  <div className="mt-3 flex flex-wrap gap-2">
                    <Chip tone="mint" icon="💉">Vacunas al día</Chip>
                    <Chip tone="blue" icon="🩺">Sin enfermedades</Chip>
                    <Chip tone="coral">Sociable</Chip>
                  </div>
                </div>
              </div>
            </div>

            {/* Decorative floating tag */}
            <div className="absolute -left-4 top-10 hidden rotate-[-6deg] rounded-2xl bg-[#0B2D4D] px-4 py-3 text-white shadow-xl md:block">
              <p className="text-[10px] font-bold uppercase tracking-widest text-[#67D5B5]">Verificado</p>
              <p className="font-display text-lg font-black">
                <span className="text-[#67D5B5]">
                  <ShieldIcon size={16} className="inline-block align-[-2px]" />
                </span>{" "}
                Dueño responsable
              </p>
            </div>
          </div>
        </div>

        {/* Trust row */}
        <section className="mt-16 rounded-[32px] border border-slate-100 bg-white p-6 shadow-sm md:p-8">
          <div className="grid gap-5 md:grid-cols-3">
            <TrustRow
              emoji="🛡️"
              title="Comunicación protegida"
              text="Bloqueamos compartir teléfonos, correos o redes sociales en el chat."
            />
            <TrustRow
              emoji="🩺"
              title="Salud al día"
              text="Verificamos vacunas, desparasitación y controles veterinarios recientes."
            />
            <TrustRow
              emoji="📍"
              title="Encuentros públicos"
              text="Recomendamos siempre el primer encuentro en un lugar público y seguro."
            />
          </div>
        </section>
      </main>
    </div>
  );
}

function Benefit({ emoji, label }) {
  return (
    <div className="flex items-center gap-3 rounded-2xl border border-slate-100 bg-white p-3 shadow-sm">
      <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-emerald-50 text-xl">
        {emoji}
      </span>
      <span className="text-sm font-bold text-[#0B2D4D]">{label}</span>
    </div>
  );
}

function TrustRow({ emoji, title, text }) {
  return (
    <div className="flex items-start gap-3">
      <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-rose-50 text-xl">
        {emoji}
      </span>
      <div>
        <p className="font-display text-lg font-black text-[#0B2D4D]">{title}</p>
        <p className="mt-1 text-sm leading-relaxed text-slate-500">{text}</p>
      </div>
    </div>
  );
}
